import type React from "react";
import { useState } from "react";

import { capitalize } from "lodash";
import styled from "styled-components";

import { mixins } from "@similarweb/styles";
import { HomepageBodyText } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { shouldSeeReactivationBanner } from "app/common/utils";
import { ReactivateAccountDunningsSection } from "app/components/Dunnings/ReactivateAccountDunningsSection/ReactivateAccountDunningsSection";
import { RestartTrialDunningsSection } from "app/components/Dunnings/RestartTrialDunningsSection/RestartTrialDunningsSection";
import { useDunnings } from "app/components/hooks/useDunnings";
import { MarketingModals } from "app/components/modals/marketing-modals/MarketingModals";
import { i18nFilter } from "app/filters/i18nFilter";
import { AfterTrialBanner } from "app/pages/digital-suite/home-page/components/AfterTrialBanner";
import { AfterTrialBannerB } from "app/pages/digital-suite/home-page/components/AfterTrialBannerB";
import { AfterTrialBannerReactivationV2 } from "app/pages/digital-suite/home-page/components/AfterTrialBannerReactivationV2";
import { ExtensionAfterTrialBanner } from "app/pages/digital-suite/home-page/components/ExtensionAfterTrialBanner/ExtensionAfterTrialBanner";
import { UpgradeCta } from "app/pages/digital-suite/home-page/components/UpgradeCta";
import { AssetsService } from "app/services/AssetsService";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import { AutocompleteDigitalMarketing } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketing";

import { MajorNavigationTile } from "./components/MajorNavigationTile";
import { basicReportsForAfterTrial, navigationTiles } from "./constants";
import {
    PageContainer,
    TilesGroup,
    HeaderContentContainer,
    AutocompleteWrapper,
    PrimaryHomepageHeaderStyled,
    HeaderTitle,
    HeaderSubtitle,
    TilesContainer,
    HeaderTitleRow,
} from "./styledComponents";
import { allTrackers } from "app/services/track/track";

const AfterTrialBannerWrapper = styled.div`
    min-width: 980px;
    max-width: 1060px;
    margin-top: 44px;
    margin-bottom: 27px;
`;

const HomepageBodyTextStyled = styled(HomepageBodyText)`
    display: block;
    ${mixins.setFont({ $size: 20, $weight: 500, $family: "'DM Sans'" })};
    margin: 50px 0 24px 40px;
`;

const HomepageHeaderStyled = styled(PrimaryHomepageHeaderStyled)`
    position: unset;
`;

const headerImageUrl = AssetsService.assetUrl("/images/secondary-home-page-header-light-small.png");

interface DigitalSuiteAfterTrialHomePageProps {
    onHomepageButtonClick: () => void;
}

export const DigitalSuiteAfterTrialHomePage: React.FC<DigitalSuiteAfterTrialHomePageProps> = () => {
    const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
    const i18n = i18nFilter();
    const userFirstName = capitalize(swSettings?.user?.firstname as string);
    const basicReportsForAfterTrialKeys = basicReportsForAfterTrial.map((b) => b.id);
    const { hasDunnings, isAfterTrialDunningsWithTrial, isAfterTrialDunningsWithoutTrial } = useDunnings();
    // hwx-3277: variant gets the new banner + 3-package modal; remove this switch and AfterTrialBannerReactivationV2 once the test concludes.
    const isReactivationPackagesV2 = GrowthbookService.growthbookInstance.isOn(
        TestsNames.REACTIVATION_PACKAGES_MODAL_V2,
    );

    const onTileClick = (title: string) => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", `Hook PRO/homepage/${i18n(title) || ""}`);

        goToPackagesPage();
    };

    return (
        <PageContainer className="main-homepage-container">
            <MarketingModals />

            {/* @ts-expect-error - bgColor is not defined on the type (should be optional in ui-components) */}
            <HomepageHeaderStyled imageUrl={headerImageUrl}>
                <HeaderContentContainer>
                    <div>
                        <HeaderTitleRow>
                            <HeaderTitle>
                                {i18n("aftertrial.homepage.header.title", {
                                    name: userFirstName,
                                })}
                            </HeaderTitle>
                        </HeaderTitleRow>
                        <HeaderSubtitle>{i18n("digitalmarketing.homepage.header.subtitle")}</HeaderSubtitle>
                    </div>
                    <AutocompleteWrapper>
                        <AutocompleteDigitalMarketing
                            includeKeywordsData={false}
                            maxCompareItemsToDisplay={1}
                            onAutocompleteOpen={() => setAutocompleteOpen(true)}
                            onAutocompleteClose={() => setAutocompleteOpen(false)}
                        />
                    </AutocompleteWrapper>
                </HeaderContentContainer>
            </HomepageHeaderStyled>

            {hasDunnings ? (
                <>
                    {isAfterTrialDunningsWithTrial ? <RestartTrialDunningsSection /> : null}
                    {isAfterTrialDunningsWithoutTrial ? <ReactivateAccountDunningsSection /> : null}
                </>
            ) : (
                <AfterTrialBannerWrapper>
                    {shouldSeeReactivationBanner() ? (
                        isReactivationPackagesV2 ? (
                            <AfterTrialBannerReactivationV2 />
                        ) : (
                            <AfterTrialBannerB />
                        )
                    ) : (
                        <AfterTrialBanner />
                    )}
                </AfterTrialBannerWrapper>
            )}
            <ExtensionAfterTrialBanner />
            <TilesGroup>
                <HomepageBodyTextStyled>{i18n("aftertrial.homepage.featured.free.tiles.title")}</HomepageBodyTextStyled>
                <TilesContainer tilesPerRow={4}>
                    {basicReportsForAfterTrial.map((tile) => (
                        <MajorNavigationTile
                            key={tile.title}
                            {...tile}
                            guid={"after-trial-homepage.tile.click"}
                            boxHeight={198}
                        />
                    ))}
                </TilesContainer>
            </TilesGroup>
            <TilesGroup>
                <HomepageBodyTextStyled>{i18n("aftertrial.homepage.featured.tiles.title")}</HomepageBodyTextStyled>
                <TilesContainer tilesPerRow={4}>
                    {navigationTiles
                        .filter((t) => !basicReportsForAfterTrialKeys.includes(t.id))
                        .map((tile) => (
                            <MajorNavigationTile
                                key={tile.title}
                                {...tile}
                                boxHeight={200}
                                guid={"after-trial-homepage.tile.click"}
                                CtaComponent={UpgradeCta()}
                                onTileClick={onTileClick}
                            />
                        ))}
                </TilesContainer>
            </TilesGroup>
        </PageContainer>
    );
};
